import { type MutableRefObject, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { collectLinkableFrames } from '@/lib/text-linking/linkable-text-frame'
import { logger } from '@/utils/logger'
import { resolveChainSelectMembers } from '../helpers/chain-select-members'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/** Ctrl+A inside a linked text frame: select the whole story across the chain. */
export function useChainSelectAll({
  internalShapeManagerRef,
  linkedTextChains,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  linkedTextChains: CenterCanvasConfig['linkedTextChains']
}) {
  /**
   * Ctrl+A inside a linked frame: select the whole story as TEXT, across every eligible member,
   * WITHOUT leaving edit mode. Returns whether it handled the gesture.
   *
   * Lives in CenterCanvas because it is the only level that holds the chain state and the manager
   * ref.
   *
   * This used to object-select the members and call `exitShapeTextEdit()` first (WMPCM-9070
   * Ruling 2). Killing the session is what made the Ctrl+X users press next fall through to the
   * OBJECT cut, deleting every frame in the chain instead of emptying it (WMPCM-9396 Bug 1).
   *
   * Ruling 2's stated purpose still holds: a following Type-panel change fans across the whole
   * chain because `applyShapeTextRunStyle` tests the cross-frame selection BEFORE its
   * editing-frame branch and styles each member's own slice. So the chain-wide reach now rides on
   * a text selection instead of an object selection, and cut/copy/delete inherit it too.
   */
  const handleSelectChainMembers = useCallback(
    (frameId: string): boolean => {
      const smgr = internalShapeManagerRef.current
      const frames = collectLinkableFrames(smgr?.getShapes() ?? [])
      const resolution = resolveChainSelectMembers(frames, frameId, linkedTextChains)

      // Every bail below falls back to the editing frame's own select-all, which looks identical
      // on screen to a broken chain select-all. Only the ones that mean the chain state and the
      // shape list actually disagree are logged (WMPCM-6867); the ones a perfectly healthy solo
      // frame hits on every Ctrl+A — no uid, no chains in scope, not in a chain — stay silent.
      if (resolution.kind === 'no-frame') {
        logger.warn('[chain-select] no linkable frame for id', frameId)

        return false
      }

      if (resolution.kind === 'solo') {
        return false
      }

      if (resolution.kind === 'no-members') {
        logger.warn('[chain-select] chain has no members mounted on this spread', frameId)

        return false
      }

      const { shapeMemberIds } = resolution
      const seeded = smgr?.seedChainTextSelectionOverFrames(shapeMemberIds) ?? false

      if (!seeded) {
        // Overwhelmingly the `frameIds.length < 2` case: a chain whose other members live on a
        // different spread. Falling back to in-frame select-all is correct there — the log is so
        // the next reader doesn't mistake it for the feature being broken.
        logger.warn(
          '[chain-select] seed declined; falling back to in-frame select-all',
          frameId,
          shapeMemberIds.length
        )
      }

      return seeded
    },
    [linkedTextChains, internalShapeManagerRef]
  )

  return { handleSelectChainMembers }
}
